//
//  src/components/directions/Directions.js
//
import * as React from "react";
import { useEffect, useState } from "react";
import "./Directions.css";


//
//  image[s]
//
import image1 from "../../images/location/1 location street sidewalk white building utility pole urban 1280x2276.webp";
import image2 from "../../images/location/2 location street barber sidewalk urban architecture 1280x2276.webp";
import image3 from "../../images/location/3 location urban sidewalk street architecture storefront white car trees 1280x2276.webp";
import image4 from "../../images/location/4 location melbourne street sidewalk dark wall urban trees parked cars 1280x2276.webp";
import image5 from "../../images/location/5 location armadale high street alleyway compendium tattoo entrance cobblestone architecture 1000x1634.webp";
import image6 from "../../images/location/6 location armadale northcote road compendium tattoo entrance brick wall grey door 1000x1778.webp.webp";
import image7 from "../../images/location/7 location armadale high street compendium tattoo interior studio art lounge industrial architecture 1000x1334.webp.webp";


//
//  direction[s]
//
export default function Directions() {
    const steps = [
        {
            image: image1,
            title: "Start at Tram Stop",
            description: "Begin your journey at the main street tram stop."
        },
        {
            image: image2,
            title: "Follow the Building",
            description: "Follow the building until you can turn left."
        },
        {
            image: image3,
            title: "Turn Left",
            description: "Turn left and keep following the building around."
        },
        {
            image: image4,
            title: "Look for the Alleyway",
            description: "Continue until you see a cobblestone alleyway."
        },
        {
            image: image5,
            title: "Enter the Alleyway",
            description: "Head down the cobblestone alleyway towards the red brick building."
        },
        {
            image: image6,
            title: "Enter the Black Door",
            description: "Enter the black door on the red brick building to access the studio."
        },
        {
            image: image7,
            title: "Welcome to Our Studio",
            description: "You've arrived! Welcome inside Compendium Tattoo."
        }
    ];

    const [copied, setCopied] = useState(false);
    const addressText = "909B High St, Armadale VIC 3143";

    const handleCopyAddress = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(addressText);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                // Fallback for older browsers or iOS Safari
                const textArea = document.createElement('textarea');
                textArea.value = addressText;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    document.execCommand('copy');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                } catch (err) {
                    console.error('Fallback copy failed:', err);
                }
                
                document.body.removeChild(textArea);
            }
        } catch (err) {
            console.error('Failed to copy address:', err);
        }
    };

    useEffect(() => {
        const stepElements = document.querySelectorAll(".direction-step");

        const fadeObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("visible");
                    }
                });
            },
            { threshold: 0.2 }
        );

        const centerObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    entry.target.classList.toggle("centered", entry.isIntersecting);
                });
            },
            { rootMargin: "-30% 0px -30% 0px", threshold: 0 }
        );

        stepElements.forEach((el) => {
            fadeObserver.observe(el);
            centerObserver.observe(el);
        });

        return () => {
            stepElements.forEach((el) => {
                fadeObserver.unobserve(el);
                centerObserver.unobserve(el);
            });
        };
    }, []);

    return (
        <section className="directions-section">
            <div className="directions-container">
                <div className="directions-guide-header">
                    <h3 className="directions-guide-title">Address</h3>                   
                    <address 
                        className={`directions-address-text ${copied ? 'copied' : ''}`}
                        onClick={handleCopyAddress}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            handleCopyAddress(e);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCopyAddress(e);
                            }
                        }}
                        aria-label="Tap to copy address to clipboard"
                        title={copied ? "Copied!" : "Tap to copy"}
                    >
                        {copied ? "✓ Copied!" : addressText}
                    </address>
                </div>

                <div className="directions-guide-header">
                    <h3 className="directions-guide-title">Finding Your Way</h3>                   
                    <p className="directions-intro">
                        Follow this visual guide from the street to our studio entrance.
                        <br />
                        Each step brings you closer to your next tattoo.
                    </p>
                </div>

                <div className="directions-timeline">
                    {steps.map((step, index) => (
                        <div 
                            key={index} 
                            className="direction-step"
                        >
                            <div className="step-number">
                                <span>{String(index + 1).padStart(2, '0')}</span>
                            </div>
                            <div className="step-content">
                                <div className="step-image-wrapper">
                                    <img 
                                        src={step.image} 
                                        alt={step.title}
                                        className="step-image"
                                        loading="lazy"
                                    />
                                    <div className="step-overlay"></div>
                                </div>
                                <div className="step-info">
                                    <h3 className="step-title">{step.title}</h3>
                                    <p className="step-description">{step.description}</p>
                                </div>
                            </div>
                            {index < steps.length - 1 && (
                                <div className="step-connector"></div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="directions-footer">
                    <a href="/contact/" className="footer-card">
                        <svg className="footer-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div>
                            <h4>Still Lost?</h4>
                            <p>The best thing to do is fill out the contact form and we'll get back to you as soon as possible; or email Jeff at <span className="email">jeff@jefftattooer.com</span></p>
                        </div>
                    </a>
                </div>
            </div>
        </section>
    );
}
